/**
 * Onboarding — logique PURE (testée) partagée par `routes/onboarding.tsx` :
 * étapes affichées dans la barre de progression selon la carte choisie,
 * prénom proposé par défaut, matière pré-cochée quand on choisit sa classe.
 */

import { SUBJECTS, type SchoolLevel, type SubjectSlug } from '@jeraisonne/db';

/** Cartes du choix de rôle (Élève et Étudiant partagent `role: 'student'`). */
export type RoleCardId = 'student' | 'student_higher_ed' | 'parent' | 'tutor' | 'player';

/**
 * Libellés de la barre de progression, identiques pour tous les rôles sur la
 * 1re étape (« Toi »). Le prof n'a qu'une étape : son tableau de bord le guide
 * ensuite (carte « Premiers pas »).
 */
export function onboardingStepLabels(card: RoleCardId | null): string[] {
  switch (card) {
    case 'student':
      return ['Toi', 'Ta classe', 'C’est parti'];
    case 'student_higher_ed':
      return ['Toi', 'Ta formation'];
    case 'parent':
      return ['Toi', 'Tes enfants'];
    case 'player':
      return ['Toi', 'Ton niveau'];
    case 'tutor':
      return ['Toi'];
    default:
      // Avant le choix : on ne promet pas un nombre d'étapes.
      return ['Toi', 'Ton profil'];
  }
}

/** Libellé du bouton de l'étape « rôle » : le prof termine directement. */
export function roleStepSubmitLabel(card: RoleCardId | null): string {
  return card === 'tutor' ? 'Aller à mon tableau de bord' : 'Continuer';
}

/**
 * Prénom proposé dans « Comment doit-on t'appeler ? » : le nom fourni par
 * Google/Apple s'il existe, sinon la 1re partie de l'email mise en forme
 * (« camille.martin@… » → « Camille »), jamais l'identifiant brut.
 */
export function guessDisplayName(input: {
  fullName?: string | null;
  name?: string | null;
  email?: string | null;
}): string {
  const fromProvider = (input.fullName ?? input.name ?? '').trim();
  if (fromProvider) return fromProvider.slice(0, 80);
  const local = (input.email ?? '').split('@')[0] ?? '';
  const first = local.split(/[._\-+]/)[0]?.replace(/\d+/g, '') ?? '';
  if (first.length < 2) return '';
  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
}

/** Matières proposées pour une classe (toutes tant qu'aucune classe n'est choisie). */
export function subjectsForLevel(level: SchoolLevel | ''): SubjectSlug[] {
  return SUBJECTS.filter((s) => !level || s.levels.includes(level)).map((s) => s.value);
}

/**
 * Matières à garder quand la classe change : on retire celles qui n'existent
 * plus pour ce niveau et, si rien ne reste, on pré-coche les maths du niveau
 * (1re matière de la liste : « Mathématiques » au collège, « Maths Spécialité »
 * en 1re/Tle…). L'élève peut décocher.
 */
export function subjectsAfterLevelChange(
  current: SubjectSlug[],
  level: SchoolLevel,
): SubjectSlug[] {
  const available = subjectsForLevel(level);
  const kept = current.filter((s) => available.includes(s));
  if (kept.length > 0) return kept;
  return available.length > 0 ? [available[0]!] : [];
}
